import { event } from "@/utils/promises";
import { Readable } from "svelte/motion";
import { writable, Writable } from "svelte/store";

export interface CustomElement {
    apply(tag: HTMLUnknownElement): () => void;
}


function hasNode(node: Node, item: Node) {
    let current: Node|null = item;
    while (current !== null) {
        if (node === current) return true;
        current = current.parentNode;
    }
    return false;
}


export default class ComponentSet {
    map: Map<string, () => Promise<{default: CustomElement}>> = new Map();
    loaded: Set<string> = new Set();
    _loading: Writable<number> = writable(0);

    set(name: string, element: () => Promise<{default: CustomElement}>) {
        if (this.loaded.has(name)) { return; }
        this.map.set(name, element);
    }

    get loading(): Readable<number> {
        return this._loading;
    }

    async load(name: string, element: HTMLUnknownElement) {
        const loader = this.map.get(name)!;
        let module;
        try {
            this._loading.update(s => s+1);
            module = await loader();
        } finally {
            this._loading.update(s => s-1);
        }

        const observer = new MutationObserver((events) => {
            for (let event of events) {
                for (let item of Array.from(event.removedNodes)) {
                    if (hasNode(item, element)) {
                        setUnloaded();
                        observer.disconnect();
                        return;
                    }
                }
            }
        });
        observer.observe(document.documentElement, {childList: true});

        const [onUnloaded, setUnloaded] = event();
        try {
            const unloader = module.default.apply(element);
            onUnloaded(() => {
                try {
                    unloader();
                } catch (e) {
                    window.reportError(e);
                }
            });
        } catch (e) {
            window.reportError(e);
        }
    }

    _autodiscover(scope: HTMLElement) {
        (window["requestIdleCallback"] || window["requestAnimationFrame"])(() => {
            let candidates: Element[] = [scope, ...Array.from(scope.querySelectorAll("*"))];
            for (let el of candidates) {
                let tag = el.localName;
                if (tag.includes("-") && this.map.has(tag)) {
                    this.load(tag, el as HTMLUnknownElement).catch(window.reportError);
                }
            }
        });
    }

    autodiscover(scope: HTMLElement) {
        this._autodiscover(scope);

        const observer = new MutationObserver((list) => {
            for (let { addedNodes, type } of list) {
                if (type !== "childList") continue;
                for (let node of Array.from(addedNodes).filter(n => n instanceof HTMLElement)) {
                    this._autodiscover(node as HTMLElement);
                }
            }
        });
        observer.observe(scope, {childList: true, subtree: true});
    }
}
